/**
 * Aurebesh, as decoration.
 *
 * WHAT THIS IS AND IS NOT, because the distinction is his and it shapes the whole file. He asked for
 * the script as DECORATION, not as a readable transliteration. So this is a set of glyphs drawn in
 * the Aurebesh style and composed into strips; it does not claim to spell anything, and no reader is
 * invited to decode it. That is why the API takes a `seed` rather than a `text`: a component that
 * accepted English would imply a mapping it does not have, and someone would eventually trust it.
 *
 * WHY SVG PATHS RATHER THAN A FONT. There is no licensed Aurebesh face to load, and inventing
 * squiggles in a normal font would look worse than not trying. The alphabet is entirely straight
 * lines and hard corners, so it draws cleanly as paths at any size, inherits `currentColor` for
 * free, and costs bytes rather than a font request the artifact CSP would refuse anyway.
 *
 * WHERE IT BELONGS. Frame furniture only: rail footers, panel corners, the masthead's second line.
 * Never in a place where text is doing work, because a strip of undecodable characters beside a
 * number he needs at 6am is noise wearing a costume.
 */

/*
 * Sixteen glyphs, on a 10-unit box, taken from the shapes that read unambiguously in the chart he
 * sent. Sixteen rather than twenty-six on purpose: the ones I could not draw with confidence would
 * have been approximations, and a wrong letter in a real alphabet is worse than a smaller set used
 * well.
 */
const GLYPHS: readonly string[] = [
  'M1 9 5 1 9 9M3 6h4',        // aurek
  'M1 1h6l2 2v2H3v4h6',        // besh
  'M1 1v6h3V3M6 1v8h3',        // cresh
  'M1 1h8L4 9',                // dorn
  'M1 1 5 9 9 1M5 9V4',        // esk
  'M2 1h7M5 1v8M2 5h6',        // forn
  'M1 3 4 1v8M6 1h3v8',        // grek
  'M1 2h8M1 5h8M1 8h8',        // herf
  'M3 2 5 1v8',                // isk
  'M1 9 4 1h5L6 9z',           // jenth
  'M9 1H2v8h7M2 5h4',          // krill
  'M2 1v6l3 2 4-4',            // leth
  'M1 9V1l4 5 4-5v8',          // nern
  'M3 1h4l2 2v4l-2 2H3L1 7V3z',// osk
  'M1 1h5l3 3-3 3H3v2',        // peth
  'M9 1H3L1 4v2l2 3h4V6H5',    // qek
];

export interface AurebeshProps {
  /** How many glyphs. Frame furniture, so short: four to ten. */
  length?: number;
  /** Any stable string. The same seed always draws the same strip, so nothing flickers on rerender. */
  seed: string;
  /** Glyph height in pixels. */
  size?: number;
  className?: string;
}

/** Deterministic pick, so a strip is stable across renders and identical between server and client. */
function indexFor(seed: string, i: number): number {
  let h = 2166136261;
  for (let c = 0; c < seed.length; c += 1) h = (h ^ seed.charCodeAt(c)) * 16777619;
  h = (h ^ (i * 2654435761)) >>> 0;
  return h % GLYPHS.length;
}

export function Aurebesh({ length = 7, seed, size = 13, className }: AurebeshProps): JSX.Element {
  const w = size * 0.72;
  const gap = size * 0.34;
  const total = length * w + (length - 1) * gap;
  return (
    <svg
      className={className === undefined ? 'mo-aurebesh' : `mo-aurebesh ${className}`}
      width={total}
      height={size}
      viewBox={`0 0 ${length * 13 - 3} 10`}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.3}
      strokeLinecap="square"
      aria-hidden="true"
      focusable="false"
    >
      {Array.from({ length }, (_, i) => (
        <g key={i} transform={`translate(${i * 13} 0)`}>
          <path d={GLYPHS[indexFor(seed, i)]!} />
        </g>
      ))}
    </svg>
  );
}
